import {
  CommandManager,
  type CommandRequest,
  type CommandResult,
  type Vehicle,
} from '@hgc/core';
import type { ConfirmDialogOptions } from '@hgc/ui';
import { useConfirm } from '@hgc/ui';
import { useCallback, useEffect, useState } from 'react';

import { useAppStore } from '../runtime/AppStoreContext';
import { usePrimaryVehicle } from '../runtime/hooks';
import type { CommandFeedback } from './model';

/**
 * 指令通道：把界面上的按钮接到 `CommandManager` 上。
 *
 * **发指令一律走管理器**，界面从不自己组帧。理由是那些看不见但要命的东西全在它里面：
 * `COMMAND_LONG` / `COMMAND_INT` 的选择（经纬度精度）、`confirmation` 递增的重传语义、
 * ACK 的归属、以及"飞控为什么拒绝"（`STATUSTEXT` 与 ACK 的关联）。
 * 少走一层就会少一样，而缺的那一样只会在真机上表现出来。
 *
 * 管理器**每辆焦点机体一个**，并且随组件卸载销毁：它订阅了车辆的事件总线，
 * 不销毁的话每换一次焦点机体就多一份永久订阅 —— 表现是"越用越卡"，没有任何报错。
 */

/** 焦点机体。`usePrimaryVehicle()` 只给摘要，命令管理器要的是车辆本身（它要订阅事件总线）。 */
export function useFocusedVehicle(): Vehicle | null {
  const store = useAppStore();
  const summary = usePrimaryVehicle();
  if (summary === null) return null;
  return store.registry?.get(summary.id) ?? null;
}

export interface CommandRunner {
  readonly manager: CommandManager | null;
  /** 有命令正在等确认。串行队列里"上一条还没落定"是用户能感知的状态，所以要能显示出来 */
  readonly busy: boolean;
  /** 最近一条命令的结论（成功与失败都有）。失败时 `text` 里是飞控给出的具体原因。 */
  readonly feedback: CommandFeedback | null;
  dismissFeedback(): void;
  /**
   * 按钮现在为什么不能点。`undefined` = 可以点。
   *
   * 刻意**忽略** `needs-confirmation`：那条 blocker 的含义是"点了要先弹确认框"，
   * 不是"不能点"。把它算成禁用会让所有危险按钮永久变灰，然后用 `confirmed: true`
   * 绕过确认 —— 那正是二次确认要防的事。
   */
  disabledReason(request: CommandRequest): string | undefined;
  /** 下发。永远不会 reject：命令没能执行是一种结果，不是异常。 */
  execute(request: CommandRequest): Promise<CommandResult | null>;
}

export function labelOf(label: string | null, commandName: string | null): string {
  if (label !== null && label !== '') return label;
  if (commandName !== null && commandName !== '') return commandName;
  return '命令';
}

/**
 * 构造一条命令请求，并决定用哪种帧格式。
 *
 * 关于 `forceLong`（一处刻意的、写在明处的取舍）：
 * `COMMAND_INT` 的唯一好处是经纬度不丢精度，代价是**没有 `confirmation` 字段**
 *（上游定义如此，见 `gate.ts`），因此丢了"这是重传"这个标记。
 * 当这次下发**根本没带坐标**时（例如"在原地起飞"、"绕当前位置"），
 * 两种帧的坐标完全一致，唯一差别就是那个重传标记 —— 所以此时明确走 LONG。
 * 带坐标的命令不受影响，仍由管理器按能力位优先选 `COMMAND_INT`。
 */
export function requestOf(command: string, params?: readonly (number | undefined)[]): CommandRequest {
  const hasCoordinate = Number.isFinite(params?.[4]) || Number.isFinite(params?.[5]);
  return {
    command,
    ...(params === undefined ? {} : { params }),
    ...(hasCoordinate ? {} : { forceLong: true }),
  };
}

/**
 * 二次确认的文案。
 *
 * 就是统一弹窗系统 `ConfirmDialogOptions` 去掉锚点（锚点由调用点的
 * `event.currentTarget` 提供）。刻意用 `Omit` 而不是自己再抄一份字段：
 * 抄一份的那一天，`@hgc/ui` 新增一项必填信息，这边的弹窗就会缺那一项。
 */
export type ConfirmPrompt = Omit<ConfirmDialogOptions, 'anchor'>;

/**
 * "确认 → 下发"的唯一实现。
 *
 * 抽成一个 hook 是为了让每个按钮的处理函数长度一致：调用点之间唯一允许的差别是
 * **确认文案**，而不是"有没有确认"。少写一次 `confirm()`，那个按钮就变成了一键触发。
 * `confirmed: true` 只在这里出现，并且只在用户真的点了确认之后 ——
 * 管理器会拦下缺这个标记的危险命令，那正是它的作用。
 *
 * `useConfirm()` 来自 `@hgc/ui` 的统一弹窗系统：按钮确认气泡锚在触发按钮上、
 * 从按钮里飞出来、位置对该按钮固定。这里只负责把锚点原样传下去。
 */
export function useCommandDispatch(
  runner: CommandRunner,
): (request: CommandRequest, prompt: ConfirmPrompt, anchor: HTMLElement) => Promise<void> {
  const confirm = useConfirm();
  return useCallback(
    async (request, prompt, anchor) => {
      const accepted = await confirm({ ...prompt, anchor });
      if (!accepted) return;
      await runner.execute({ ...request, confirmed: true });
    },
    [confirm, runner],
  );
}

export function useCommandRunner(): CommandRunner {
  const vehicle = useFocusedVehicle();
  const [manager, setManager] = useState<CommandManager | null>(null);
  const [feedback, setFeedback] = useState<CommandFeedback | null>(null);
  const [inFlight, setInFlight] = useState(0);

  useEffect(() => {
    if (vehicle === null) {
      setManager(null);
      return;
    }
    const created = new CommandManager({ vehicle });
    setManager(created);
    return () => {
      setManager(null);
      created.dispose();
    };
  }, [vehicle]);

  useEffect(() => {
    if (manager === null) {
      setFeedback(null);
      return;
    }
    const offs = [
      manager.events.on('failed', (event) => {
        setFeedback({
          level: 'critical',
          title: `${labelOf(event.label, event.commandName)}失败`,
          // result.text 形如「执行失败：PreArm: GPS 卫星数不足」——后半句是飞控自己说的原因
          text: event.result.text,
        });
      }),
      manager.events.on('rejected', (event) => {
        setFeedback({
          level: 'warning',
          title: `${labelOf(event.label, event.commandName)}被拦下`,
          text: event.text,
        });
      }),
    ];
    return () => {
      for (const off of offs) off();
    };
  }, [manager]);

  const disabledReason = useCallback(
    (request: CommandRequest): string | undefined => {
      if (manager === null) return '未选中机体：先连接链路并等第一帧心跳到达';
      const blockers = manager.check(request).blockers.filter((blocker) => blocker.code !== 'needs-confirmation');
      if (blockers.length === 0) return undefined;
      return blockers.map((blocker) => blocker.text).join('；');
    },
    [manager],
  );

  const execute = useCallback(
    async (request: CommandRequest): Promise<CommandResult | null> => {
      if (manager === null) return null;
      setInFlight((count) => count + 1);
      try {
        const result = await manager.execute(request);
        if (result.ok) {
          setFeedback({
            level: 'ok',
            title: `${labelOf(result.label, result.commandName)}已接受`,
            text: result.text,
          });
        }
        return result;
      } finally {
        setInFlight((count) => count - 1);
      }
    },
    [manager],
  );

  const dismissFeedback = useCallback(() => setFeedback(null), []);

  return {
    manager,
    busy: inFlight > 0,
    feedback,
    dismissFeedback,
    disabledReason,
    execute,
  };
}
